import { createPopper } from '@popperjs/core';

import { SYNC_STATUS } from './syncStatusConstants';

/**
 * Inline SVG: paths match @Sage/hoi-poi-ui `Icon` → `icons/SyncPending.jsx`.
 * Fills come from DataGrid cell styles (theme grey + semantic caution).
 */
const SYNC_PENDING_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false" class="w5-sync-list-status__svg" preserveAspectRatio="xMidYMid meet" shape-rendering="geometricPrecision">
<path fill-rule="evenodd" clip-rule="evenodd" class="w5-sync-list-status__icon-path w5-sync-list-status__icon-path--sync" d="M4.95 19.05C3.1375 17.25 2 14.7625 2 12C2 7.33751 5.1875 3.43751 9.5 2.32501V4.93751C6.5875 5.96251 4.5 8.73751 4.5 12C4.5 14.075 5.35 15.9375 6.7 17.3L9.5 14.5V22H2L4.95 19.05ZM14.5 2H22L19.05 4.95C20.8625 6.75 22 9.2375 22 12C22 13.8627 21.4912 15.6037 20.6052 17.0939L19.0294 14.6177C19.3337 13.8029 19.5 12.9209 19.5 12C19.5 9.925 18.65 8.0625 17.3 6.7L14.5 9.5V2Z"/>
<path fill-rule="evenodd" clip-rule="evenodd" class="w5-sync-list-status__icon-path w5-sync-list-status__icon-path--warning" d="M18 13C15.24 13 13 15.24 13 18C13 20.76 15.24 23 18 23C20.76 23 23 20.76 23 18C23 15.24 20.76 13 18 13ZM19.65 20.35L17.5 18.2V15H18.5V17.79L20.35 19.64L19.65 20.35Z"/>
</svg>`;

/**
 * @param {Record<string, unknown>} data
 * @returns {number|undefined}
 */
export function getSyncStatusIdFromRow(data) {
    if (!data || typeof data !== 'object') return undefined;
    const raw = data.SyncStatusId ?? data.syncStatusId ?? undefined;
    if (raw === undefined || raw === null || raw === '') return undefined;
    const n = Number(raw);
    return Number.isNaN(n) ? undefined : n;
}

/**
 * Same pattern as DataGrid `Base.js`: shared `.w5-tooltip` in the grid + Popper.
 * @param {HTMLElement} triggerEl
 * @param {string} tooltipText
 * @param {{ context?: { tableId?: string } }} [agGridParams]
 */
function attachDataGridW5Tooltip(triggerEl, tooltipText, agGridParams) {
    if (!tooltipText || !triggerEl) return;

    const tableId = agGridParams?.context?.tableId;
    const root = tableId ? document.querySelector(`#${tableId}`) : document;
    const tooltip = root?.querySelector('.w5-tooltip');

    if (!tooltip) {
        triggerEl.setAttribute('title', tooltipText);
        return;
    }

    let popperInstance = null;

    const show = () => {
        tooltip.style.display = 'block';
        if (tooltip.firstElementChild) {
            tooltip.firstElementChild.innerText = tooltipText;
        }
        if (popperInstance) {
            popperInstance.destroy();
            popperInstance = null;
        }
        popperInstance = createPopper(triggerEl, tooltip, {
            placement: 'top-start',
            modifiers: [
                {
                    name: 'offset',
                    options: {
                        offset: [0, 5],
                    },
                },
            ],
        });
    };

    const hide = () => {
        tooltip.style.display = 'none';
        if (popperInstance) {
            popperInstance.destroy();
            popperInstance = null;
        }
    };

    triggerEl.addEventListener('mouseenter', show);
    triggerEl.addEventListener('mouseleave', hide);
}

/**
 * @param {Record<string, unknown>} data
 * @returns {boolean}
 */
export function shouldShowSyncPendingReviewIcon(data) {
    return getSyncStatusIdFromRow(data) === SYNC_STATUS.PENDING_TO_REVIEW;
}

/**
 * @param {{ tooltipText: string, agGridParams?: { context?: { tableId?: string } } }} params
 * @returns {HTMLSpanElement}
 */
export function createSyncPendingIndicator({ tooltipText, agGridParams }) {
    const wrap = document.createElement('span');
    wrap.className = 'w5-sync-list-status__icon';
    wrap.setAttribute('role', 'img');
    wrap.innerHTML = SYNC_PENDING_SVG;
    if (tooltipText) {
        wrap.setAttribute('aria-label', tooltipText);
        attachDataGridW5Tooltip(wrap, tooltipText, agGridParams);
    }
    return wrap;
}
